import React, { createElement } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

interface VirtualizedContainerProps<T> {
  data: T[];
  columnCount?: number;
  renderElement: (props: T) => React.ReactNode;
  spacing?: string;
  containerStyle?: React.CSSProperties;
  estimatedRowHeight?: number;
  // Extra scrollable space appended below the last row. Needed because cards
  // can render a hover footer that is absolutely positioned below the card
  // (top: 100%); without this reserve the last row's expanded footer overflows
  // the scroll content and spawns a second scrollbar.
  bottomReserve?: number;
}
function withEstimatedRowHeight(Component: any) {
  return function WrappedComponent(props: VirtualizedContainerProps<any>) {
    const [estimatedRowHeight, setEstimatedRowHeight] = React.useState(0);
    const measurementNodeRef = React.useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() => {
      if (!measurementNodeRef.current || !props.data?.[0]) return;
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          setEstimatedRowHeight(height);
          if (height > 0) observer.disconnect();
        }
      });
      observer.observe(measurementNodeRef.current);
      return () => observer.disconnect();
    }, []);
    return (
      <>
        {estimatedRowHeight ? (
          <Component {...{ ...props, estimatedRowHeight }} />
        ) : null}

        {/* Measurement element */}
        {!estimatedRowHeight ? (
          <div
            ref={measurementNodeRef}
            style={{
              position: "absolute",
              top: "-1000px",
              left: 0,
              height: "max-content",
              width: "100%",
              visibility: "hidden",
              pointerEvents: "none",
              zIndex: -1,
            }}
          >
            {props.data?.[0] && props.renderElement(props.data[0])}
          </div>
        ) : null}
      </>
    );
  };
}

export const VirtualizedContainer = withEstimatedRowHeight(
  function VirtualizedContainer<T>(props: {
    data: T[];
    columnCount?: number;
    renderElement: (props: T) => React.ReactNode;
    spacing?: string;
    containerStyle?: React.CSSProperties;
    estimatedRowHeight?: number;
    bottomReserve?: number;
  }) {
    // refs
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = React.useState(0);
    const rowHeights = React.useRef<Map<number, number>>(new Map());
    const rowRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const containerHeight = props?.containerStyle?.height || 450;
    const estimatedRowHeight = props?.estimatedRowHeight || 300;

    // ResizeObserver to track container width
    React.useLayoutEffect(() => {
      if (!scrollContainerRef.current) return;
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      resizeObserver.observe(scrollContainerRef.current);
      return () => resizeObserver.disconnect();
    }, []);

    const columnCount = props?.columnCount || 1;
    const columnWidth = containerWidth / columnCount;

    // Prepare rows data for virtualization (group items by rows)
    const rowData = React.useMemo(() => {
      const rows: any[][] = [];
      for (let i = 0; i < props.data.length; i += columnCount) {
        rows.push(props.data.slice(i, i + columnCount));
      }
      return rows;
    }, [props.data, columnCount]);

    const rowVirtualizer = useVirtualizer({
      count: rowData.length,
      getScrollElement: () => scrollContainerRef.current,
      estimateSize: (index) => rowHeights.current.get(index) || estimatedRowHeight, // prettier-ignore
      overscan: 5,
      enabled: true,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    return (
      <div
        id="virtualized-inv-grid-v2"
        ref={scrollContainerRef}
        style={{
          width: "100%",
          height: containerHeight,
          overflowY: "auto",
          contain: "strict",
          ...props?.containerStyle,
        }}
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize() + (props?.bottomReserve || 0)}px`, // prettier-ignore
            width: "100%",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualItems[0]?.start || 0}px)`,
            }}
          >
            {virtualItems.map((virtualRow) => {
              const rowItems = rowData[virtualRow.index];

              return (
                <div
                  key={virtualRow.index}
                  ref={(el) => {
                    rowRefs.current[virtualRow.index] = el;
                  }}
                  data-row-index={virtualRow.index}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    columnGap: props?.spacing || "16px",
                    marginBottom: props?.spacing || "16px",
                  }}
                >
                  {rowItems.map((rowItemProps, columnIndex) => {
                    return (
                      <div
                        key={`${virtualRow.index}-${columnIndex}`}
                        style={{
                          width: `${columnWidth}px`,
                          boxSizing: "border-box",
                        }}
                      >
                        {rowItemProps && props?.renderElement
                          ? props?.renderElement(rowItemProps)
                          : null}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);
